import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Modal, Select, Form, Button, DatePicker, TimePicker } from '@maxtropy/components';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { controlOptions } from '../utils';
import styles from './index.module.scss';
import {
  apiV2GgdBatchOperateTimeTaskPost,
  apiV2GgdDeviceListPost,
  apiV2GgdTimeTaskPost,
} from '@maxtropy/device-customer-apis-v2';
import { useRequest } from 'ahooks';
import { Space } from 'antd';
import { isNil } from 'lodash-es';

interface ScheduledTaskModalProps {
  id?: number;
  deviceId?: number;
  open: boolean;
  setOpen: (open: boolean) => void;
  updateFn: () => void;
}

const ScheduledTaskModal: React.FC<ScheduledTaskModalProps> = props => {
  const { open, deviceId, id, setOpen, updateFn } = props;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isNil(id)) return;
    apiV2GgdTimeTaskPost({ id }).then(res => {
      form.setFieldsValue({
        specificTimeTask: (res.specificTimeTask ?? []).map(item => ({
          status: item.status,
          time: dayjs(item.time),
        })),
        everyDayTask: (res.dailyTimeTask ?? []).map(item => ({
          status: item.status,
          time: dayjs(item.time, 'HH:mm'),
        })),
      });
    });
  }, [id]);

  const { data: deviceList } = useRequest(async () => {
    const res = await apiV2GgdDeviceListPost({});
    return res.list;
  });

  const deviceOptions = useMemo(() => {
    return (deviceList ?? []).map(item => ({
      label: item.deviceName,
      value: item.deviceId,
    }));
  }, [deviceList]);

  const onSave = (isDistribute: boolean) => {
    if (isDistribute) {
      setLoading(true);
    }
    form.validateFields().then((v: any) => {
      apiV2GgdBatchOperateTimeTaskPost({
        deviceIds: isNil(deviceId) ? v.deviceIds : [deviceId],
        specificTimeTask: (v.specificTimeTask ?? []).map((m: any, index: number) => ({
          time: dayjs(m.time).startOf('second').format('YYYY-MM-DD HH:mm:ss'),
          status: m.status,
        })),
        dailyTimeTask: (v.everyDayTask ?? []).map((m: any) => ({
          time: dayjs(m.time).startOf('second').format('HH:mm:ss'),
          status: m.status,
        })),
        distribute: isDistribute,
      })
        .then(() => {
          setOpen(false);
          updateFn();
        })
        .finally(() => {
          setLoading(false);
        });
    });
  };

  const checkSpecificTimeDuplicate = (_: any, value: any) => {
    if (!value) return Promise.resolve();
    const list = form.getFieldValue('specificTimeTask') || [];
    const formatted = dayjs(value).format('YYYY-MM-DD HH:mm');
    const duplicates = list.filter((i: any) => i?.time && dayjs(i.time).format('YYYY-MM-DD HH:mm') === formatted);
    if (duplicates.length > 1) {
      return Promise.reject(new Error('与其他新增时间冲突'));
    }
    return Promise.resolve();
  };

  const checkEverydayTimeDuplicate = (_: any, value: any) => {
    if (!value) return Promise.resolve();
    const list = form.getFieldValue('everyDayTask') || [];
    const formatted = dayjs(value).format('HH:mm');
    const duplicates = list.filter((i: any) => i?.time && dayjs(i.time).format('HH:mm') === formatted);
    if (duplicates.length > 1) {
      return Promise.reject(new Error('与其他新增时间冲突'));
    }
    return Promise.resolve();
  };

  return (
    <Modal
      title={isNil(deviceId) ? '批量设置定时任务' : '定时任务'}
      open={open}
      size="large"
      contentClassName="modal-form-content"
      onCancel={() => {
        setOpen(false);
      }}
      footer={
        <Space size={8}>
          <Button onClick={() => setOpen(false)}>取消</Button>
          <Button loading={loading} disabled={loading} type="primary" onClick={() => onSave(true)}>
            {loading ? '正在下发中' : '保存并下发'}
          </Button>
          <Button type="primary" disabled={loading} onClick={() => onSave(false)}>
            保存
          </Button>
        </Space>
      }
    >
      <Form form={form} labelAlign="left">
        {!isNil(deviceId) ? null : (
          <Form.Item name="deviceIds" label="选择设备" rules={[{ required: true, message: '请选择设备' }]}>
            <Select
              placeholder="请选择"
              options={deviceOptions}
              mode="multiple"
              allowClear
              optionFilterProp="label"
              showSearch
            />
          </Form.Item>
        )}

        <Form.Item label="指定时间定时任务">
          <div className={styles.headerTitle}>
            <div>控制类型</div>
            <div>控制时间</div>
          </div>
          <Form.List name="specificTimeTask">
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name }) => (
                  <div key={key} style={{ display: 'flex', columnGap: 8, marginBottom: 8 }}>
                    <Form.Item name={[name, 'status']} rules={[{ required: true, message: '请选择' }]}>
                      <Select style={{ width: 220 }} placeholder="请选择" options={controlOptions} />
                    </Form.Item>
                    <Form.Item
                      name={[name, 'time']}
                      style={{ flex: 1 }}
                      rules={[{ required: true, message: '请选择时间' }, { validator: checkSpecificTimeDuplicate }]}
                    >
                      <DatePicker
                        showTime={{ format: 'HH:mm' }}
                        showSecond={false}
                        placeholder="请选择"
                        style={{ width: '100%' }}
                      />
                    </Form.Item>
                    <MinusCircleOutlined style={{ fontSize: 16, color: '#4A90E2' }} onClick={() => remove(name)} />
                  </div>
                ))}

                <Form.Item>
                  <Button
                    type="dashed"
                    wrapStyle={{ width: '100%' }}
                    disabled={fields.length >= 5}
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    ghost
                  >
                    新增定时任务
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>

        <Form.Item label="每日定时任务">
          <div className={styles.headerTitle}>
            <div>控制类型</div>
            <div>控制时间</div>
          </div>
          <Form.List name="everyDayTask">
            {(fields, { add, remove }, { errors }) => (
              <>
                {fields.map(({ key, name }, index) => (
                  <div key={key} style={{ display: 'flex', columnGap: 8, marginBottom: 8 }}>
                    <Form.Item
                      name={[name, 'status']}
                      rules={[
                        { required: true, message: '请选择' },
                        {
                          validator: async (_, value) => {
                            const list = form.getFieldValue('everyDayTask') || [];
                            const duplicate = list.filter(
                              (item: any, i: number) => i !== index && item?.status === value
                            );
                            if (duplicate.length > 0) {
                              return Promise.reject(new Error('控制类型不能重复'));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Select style={{ width: 220 }} placeholder="请选择" options={controlOptions} />
                    </Form.Item>
                    <Form.Item
                      name={[name, 'time']}
                      style={{ flex: 1 }}
                      rules={[{ required: true, message: '请选择时间' }, { validator: checkEverydayTimeDuplicate }]}
                    >
                      <TimePicker showSecond={false} placeholder="请选择" style={{ width: '100%' }} />
                    </Form.Item>
                    <MinusCircleOutlined style={{ fontSize: 16, color: '#4A90E2' }} onClick={() => remove(name)} />
                  </div>
                ))}

                <Form.Item>
                  <Button
                    type="dashed"
                    wrapStyle={{ width: '100%' }}
                    disabled={fields.length >= 2}
                    onClick={() => add()}
                    icon={<PlusOutlined />}
                    ghost
                  >
                    新增定时任务
                  </Button>
                  <Form.ErrorList errors={errors} />
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ScheduledTaskModal;
